<template>
    <CheckIfHorseExistDialog v-model="showExistingHorsesDialog" :similarHorses="similarHorses"
                             external-horse @useHorse="useHorse" />
    <div class="imported-horse-form flex flex-column gap-4">
        <ConfirmDialog></ConfirmDialog>
        <VeeForm @submit="submitHorse" @keydown.enter.prevent="handleEnterKey" v-slot="{ isSubmitting,errors }">
            <h2 class="page-header">{{$t('message.horseCreate.importedHorseHeader')}}</h2>
            <HorseEditMainMainData v-model="horse" imported @importPhoto="importPhoto"
                                   @color-validation-result="handleColorValidation"/>
            <HorseEditMainDates ref="dates" v-model="horse" imported />
            <div class="separator"/>
            <HorseEditOwners ref="ownersComponent" v-model="horse"
                             :breeders-error-message="!validateBreeders() && wasValidated"
                             :owners-error-message="!validateOwners() && wasValidated"
            />
            <div class="separator"/>
            <HorseEditMainPedigree v-model="horse"/>
            <div class="separator"/>
            <HorseEditMainStudbooks v-model="horse" :display-stud-book-status="false" :select-local-studbook="true"
                                    :imported="true"/>
            <div class="separator"/>
            <HorseEditMainOtherData v-model="horse" imported/>
            <div v-if="showCountryMissMatchWarning" class="flex justify-content-center align-items-center gap-2 mt-3">
                <i style="color: red;" class="pi pi-exclamation-triangle attention-icon" />
                <span style="color: red;">{{ $t('message.horseCreate.showCountryMissMatchWarning') }}</span>
            </div>
            <div v-if="registrationBlockedDueToColorMismatch" class="flex justify-content-center align-items-center gap-2 mt-3">
                <i style="color: red;" class="pi pi-exclamation-triangle attention-icon" />
                <span style="color: red;">{{ $t('message.horseCreate.registrationBlockedDueToColorMismatch') }}</span>
            </div>
            <div class="flex justify-content-center mt-3 gap-3">
                <CustomButton :label="$t('message.horseCreate.saveHorse')" @click="saveHorse"/>
                <CustomButton type="submit"
                              :label="horse.externalHorseId ? $t('message.horseCreate.upgradeHorse') : $t('message.horseCreate.submitHorse')"
                              secondary :disabled="isSubmitting || (!isAdmin && registrationBlockedDueToColorMismatch)"/>
            </div>
        </VeeForm>
    </div>
</template>

<script>
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import { Form, Form as VeeForm } from "vee-validate";
import ConfirmDialog from "primevue/confirmdialog";
import CustomInputText from "../../../components/form/CustomInputText.vue";
import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
import CustomSelectOneHorseBreed from "@/components/form/CustomSelectOneHorseBreed.vue";
import CustomCalendar from "../../../components/form/CustomCalendar.vue";
import CustomCheckbox from "../../../components/form/CustomCheckbox.vue";
import {
    saveImportedHorse,
    submitImportedHorse,
    getImportedHorse,
    setHorseAvatar,
    getCountries,
    getBookTypes,
    searchSimpleHorse,
} from "@/swagger/vue-api-client";
import CustomSelectOneHorseSex from "@/components/form/CustomSelectOneHorseSex.vue";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import CustomAutocompleteUser from "@/components/form/CustomAutocompleteUser.vue";
import OwnerAddDialog from "@/views/owner/OwnerAddDialog.vue";
import CustomSelectOneHorseColor from "@/components/form/CustomSelectOneHorseColor.vue";
import HorseEditMainMainData
    from "@/views/horse/horseEdit/main/components/HorseEditMainMainData.vue";
import HorseEditMainStudbooks
    from "@/views/horse/horseEdit/main/components/HorseEditMainStudbooks.vue";
import HorseEditMainOtherData
    from "@/views/horse/horseEdit/main/components/HorseEditMainOtherData.vue";
import HorseEditMainPedigree
    from "@/views/horse/horseEdit/main/components/HorseEditMainPedigree.vue";
import HorseEditOwners from "@/views/horse/horseEdit/main/components/HorseEditOwners.vue";
import HorseEditMainDates from "@/views/horse/horseEdit/main/components/HorseEditMainDates.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import {HorseStatus} from "@/utils/HorseStatus";
import {SystemRole} from "@/utils/SystemRole";
import CheckIfHorseExistDialog from "@/components/CheckIfHorseExistDialog.vue";

export default {
    name: "ImportedHorseFormView",
    components: {
        CustomButton,
        HorseEditMainDates,
        HorseEditOwners,
        HorseEditMainPedigree,
        HorseEditMainOtherData,
        HorseEditMainStudbooks,
        HorseEditMainMainData,
        CustomSelectOneHorseColor,
        OwnerAddDialog,
        CustomAutocompleteUser,
        CustomInputNumber,
        CustomSelectOneCountry,
        CustomSelectOneHorseSex,
        CustomSelectOneHorseBreed,
        CustomMultiSelectHorseColor,
        Button,
        Toolbar,
        Form,
        CustomInputText,
        CustomCalendar,
        CustomCheckbox,
        VeeForm,
        ConfirmDialog,
        CheckIfHorseExistDialog,
    },
    data() {
        return {
            showErrorMessages: false,
            horse: {
                id: null,
                name: null,
                sex: null,
                breed: null,
                colorId: null,
                dateOfBirth: null,
                countryOfBirthId: null,
                countryOfImportId: null,
                dateOfImport: null,
                ueln: null,
                transponderCode: null,
                controlledBy: null,
                dateOfControl: null,
                dnaSampleExists: false,
                previousBookTypeId: null,
                studbookRegistryId: null,
                bookNumber: null,
                bookPage: null,
                bookAppendixNumber: null,
                dateOfDeath: null,
                notes: null,
                breeders: [],
                owners: [],
                father: null,
                sireColorName: null,
                mother: null,
                damColorName: null,
            },
            dialogVisible: false,
            sameAsBreeders: false,
            loaded: false,
            importedPhoto: null,
            validationErrors: {},
            wasValidated: false,
            showCountryMissMatchWarning: false,
            countries: [],
            bookTypes: [],
            registrationBlockedDueToColorMismatch: null,
            systemRole: SystemRole.getUserRole(),
            showExistingHorsesDialog: false,
            dialogShowed: false,
            similarHorses: [],
        };
    },
    beforeMount() {
        getCountries().then((response) => {
            this.countries = response.data;
        });
        getBookTypes().then((response) => {
            this.bookTypes = response.data;
        });
        if (this.$route.params.id) {
            getImportedHorse({ id: this.$route.params.id }).then((response) => {
                this.horse = response.data;
                if (this.horse.horseStatus !== HorseStatus.NEW_IMPORTED && this.horse.horseStatus !== HorseStatus.EXTERNAL) {
                    this.$router.push({name: "forbidden"});
                }
                this.loaded = true;
            });
        } else {
            this.loaded = true;
        }
    },
    methods: {
        importPhoto(value) {
            this.importedPhoto = value;
        },
        validateOwners() {
            if (!this.horse.owners || !Array.isArray(this.horse.owners)) {
                return false;
            }
            return this.horse.owners.filter((owner) => owner !== null).length > 0;
        },
        validateBreeders() {
            if (!this.horse.breeders || !Array.isArray(this.horse.breeders)) {
                return false;
            }
            return this.horse.breeders.filter((breeder) => breeder !== null).length > 0;
        },
        validateForm() {
            this.wasValidated = true;
            const isValid = this.validateOwners() && this.validateBreeders()
                && this.$refs.dates.validateDateOfImport() && this.$refs.dates.validateDateOfControl();
            this.showErrorMessages = !isValid;
            return isValid;
        },
        setBreed() {
            if (localStorage.getItem("horseBreed") === "ARABIAN") {
                this.horse.breed = "ARABIAN";
            }
            if (localStorage.getItem("horseBreed") === "THOROUGHBRED") {
                this.horse.breed = "THOROUGHBRED";
            }
        },
        saveHorse() {
            this.setBreed();
            saveImportedHorse({ body: this.horse })
                .then((res) => {
                    this.$toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: this.$t("message.other.dataSaved"),
                        life: 3000,
                    });
                    if (this.importedPhoto) {
                        this.axios.post(
                            `${process.env.VUE_APP_API_URL}/files/photos/${res.data}`,
                            this.importedPhoto,
                            {
                                headers: {
                                    "Content-Type": "multipart/form-data",
                                },
                            },
                        )
                            .then((response) => {
                                setHorseAvatar({
                                    horseId: res.data,
                                    photoId: response.data[0],
                                });
                            });
                    }
                    this.horse.id = res.data;
                    this.$router.push({path: "/horse/import/" + this.horse.id});
                })
                .catch((err) => {
                    this.notifyError(err);
                });
        },
        async submitHorse() {
            this.setBreed();
            // Validate before submitting
            await searchSimpleHorse({
body: {
...this.horse, excludedId: this.horse.id, birthCountryId: this.horse.countryOfBirthId, horseBreed: this.horse.breed, dateOfBirth: {min: this.horse.dateOfBirth, max: this.horse.dateOfBirth},
},
})
                .then((response) => {
                    this.similarHorses = response.data;
                });
            if (!this.horse.externalHorseId && this.similarHorses.length > 0 && !this.dialogShowed) {
                this.showExistingHorsesDialog = true;
                this.dialogShowed = true;
                return;
            }
            if (this.validateForm()) {
                submitImportedHorse({ body: this.horse })
                    .then((res) => {
                        this.$toast.add({
                            severity: "success",
                            summary: "Success",
                            detail: this.$t("message.other.dataSaved"),
                            life: 3000,
                        });
                        if (this.importedPhoto) {
                            this.axios.post(
                                `${process.env.VUE_APP_API_URL}/files/photos/${res.data}`,
                                this.importedPhoto,
                                {
                                    headers: {
                                        "Content-Type": "multipart/form-data",
                                    },
                                },
                            )
                                .then((response) => {
                                    setHorseAvatar({
                                        horseId: res.data,
                                        photoId: response.data[0],
                                    }).then(() => {
                                        this.$router.push({path: "/"});
                                    });
                                });
                        } else {
                            this.$router.push({path: "/"});
                        }
                    })
                    .catch((err) => {
                        this.notifyError(err);
                    });
            }
        },
        notifyError(error) {
            if (error.response?.data) {
                for (const [key, value] of Object.entries(error.response.data)) {
                    if (key !== "path" && key !== "status" && key !== "timestamp") {
                        this.$toast.add({
                            severity: "error",
                            summary: this.$t("message.other.error"),
                            detail: value,
                            life: 5000,
                        });
                    }
                }
            } else {
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: this.$t("unknownError"),
                    life: 5000,
                });
            }
        },
        handleEnterKey(event) {
            // this body is intentionally empty
        },
        openDialog() {
            this.dialogVisible = true;
        },
        checkCountryMatch() {
            if (
                !this.horse.ueln
                || !this.horse.countryOfBirthId
                || !this.horse.previousBookTypeId
            ) {
                this.showCountryMissMatchWarning = false;
                return;
            }

            const match = this.horse.ueln.match(/[A-Z]{3}/);
            const codeAlpha3 = match ? match[0].toUpperCase() : null;
            const uelnCountryCode = this.horse.ueln.slice(0, 3);

            const birthCountry = this.countries.find((c) => c.id === this.horse.countryOfBirthId);
            const birthAlpha3 = birthCountry.alpha3.toUpperCase();
            const passportAlpha3 = this.bookTypes.find((b) => b.id === this.horse.previousBookTypeId).country?.alpha3?.toUpperCase();

            this.showCountryMissMatchWarning = !((codeAlpha3 === birthAlpha3 || uelnCountryCode === birthCountry.code)
                && birthAlpha3 === passportAlpha3);
        },
        handleColorValidation(isValid) {
            if (!isValid) {
                this.$confirm.require({
                    message: "Cannot register the foal due to color inconsistency.",
                    header: "Color inconsistency",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "OK",
                    rejectLabel: null,
                    accept: () => {
                    },
                    reject: () => {
                    },
                });
            }
            this.registrationBlockedDueToColorMismatch = !isValid;
        },
        useHorse(value) {
            this.horse.externalHorseId = value.id;
            this.showExistingHorsesDialog = false;
        },
    },
    computed: {
        isAdmin() {
            return this.systemRole === SystemRole.ADMIN;
        },
    },
    watch: {
        "horse.name": {
            handler() {
                this.dialogShowed = false;
            },
            deep: true,
        },
        "horse.breeders": {
            handler(newBreeders) {
                if (this.sameAsBreeders) {
                    this.horse.owners = newBreeders.map((breeder) => breeder);
                }
            },
            deep: true, // Enables array tracking
            immediate: true,
        },

        "horse.ueln": "checkCountryMatch",

        "horse.countryOfBirthId": {
            handler(newCountryId) {
                const bookTypeId = this.bookTypes.find((bt) => bt.countryId === newCountryId)?.id;
                if (bookTypeId != null) {
                    this.horse.previousBookTypeId = bookTypeId;
                } else {
                    this.checkCountryMatch();
                }
            },
            deep: true,
        },

        "horse.previousBookTypeId": {
            handler: "checkCountryMatch",
            deep: true,
        },

        "horse.father.colorName": function (colorName) {
            this.horse.sireColorName = colorName;
        },

        "horse.mother.colorName": function (colorName) {
            this.horse.damColorName = colorName;
        },
    },
};
</script>

<style lang="less" scoped>
.imported-horse-form {
    .separator {
        width: 100%;
        height: 1.5px;
        background-color: var(--separator-color);
    }

    .error-messages {
        margin-top: 10px;
        color: var(--error-color, #f44336);
        font-size: 0.875rem;

        .error-message {
            margin-bottom: 4px;
        }
    }
}
.hidden-reject-button {
    display: none !important;
}
.p-confirm-dialog-reject {
    display: none !important;
}
</style>
